import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { ResponseObj } from '../../main/responseObj';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { OpUserP } from '../../suite/usermanagement/data';
import { DataSharingService } from '../../main/data-sharing.service';
import { environment } from 'src/environments/environment';
import { ApplicationPackage } from 'src/app/enums/application-package.enum';
import { Service } from 'src/app/enums/service.enum';
import { CustomHttpUrlEncodingCodec } from 'src/app/main/encoder';
import { Customer, InputCustomer } from '../model/Customer';
import { CustomerQueryParams } from 'src/app/model/customer-query-params.model';
import { PageOfReportRequest } from 'src/app/model/cms-vod-core/dealing-models/page-of-report-request.model';
import { Campaign } from '../model/Campaign';
import { QueryParams } from 'src/app/model/query-models/query-models';
import { PageOfCampaigns } from '../model/page-of-campaigns.model';
import { Promotion } from '../model/Promotion';
import { PageOfPromotions } from '../model/page-of-promotions.model';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { canonicalCustomerType } from '../model/CustomerType';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  readonly baseUrl = environment.mwCrmEndpoint + Service.CRM + '/' + Service.CUSTOMER;
  readonly productUrl = environment.endPoint + Service.CRM + '/' + ApplicationPackage.PRODUCT + Service.PRODUCT;
  readonly subscriberUrl = environment.mwCrmEndpoint + Service.CRM + '/' + Service.SUBSCRIBER;
  readonly campaignUrl = environment.mwCrmEndpoint + Service.CRM + '/' + Service.CAMPAIGN;
  readonly purchaseVodsUrl = environment.mwCrmEndpoint + Service.CRM + '/' + Service.PURCHASEVODS;
  readonly reminderUrl = environment.endPoint + Service.CRM + '/' + ApplicationPackage.CMS_BTV + Service.REMINDERS;
  readonly sessionQuotaUrl = environment.mwCrmEndpoint + Service.CRM + '/' + Service.SESSION_QUOTA;
  readonly deviceUrl = environment.mwCrmEndpoint + Service.CRM + '/' + Service.USER_DEVICES;
  readonly customerRecordUrl = environment.endPoint + Service.CRM + '/' + ApplicationPackage.CMS_BTV + Service.PROGRAM;
  readonly reportUrl = environment.mwCrmEndpoint + Service.CRM + '/' + Service.REPORT;
  readonly authUrl = environment.endPoint + Service.CRM + '/' + ApplicationPackage.NEW_AUTH + Service.USER;

  private customerData = new Subject<Customer>();
  private refreshPurchase = new Subject<boolean>();

  customerDataSource$ = this.customerData.asObservable();
  refreshDataSource$ = this.refreshPurchase.asObservable();

  currentUser: OpUserP;

  private userExistsSubject = new BehaviorSubject<boolean | null>(null);
  userExists$ = this.userExistsSubject.asObservable();

  constructor(private http: HttpClient, private dataSharing: DataSharingService) {
    dataSharing.userDataSource$.subscribe((data) => {
      this.currentUser = data;
    });
  }

  sendCustomerData(data: Customer) {
    this.customerData.next(data);
  }

  refreshCustomerData(data: boolean) {
    this.refreshPurchase.next(data);
  }

  listCustomers(): Observable<ResponseObj> {
    return this.http.get(this.baseUrl);
  }

  getAllCustomersWithoutPageable(): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.baseUrl, { observe: 'response' });
  }

  getAllCustomersWithoutFilter(): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.baseUrl + "/all", { observe: 'response' });
  }

  getAllCustomers(customerQueryParams?: CustomerQueryParams): Observable<any> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (customerQueryParams.page > 0) {
      queryParams = queryParams.set('page', <any>customerQueryParams.page);
    }
    if (customerQueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>customerQueryParams.itemCount);
    }
    if (customerQueryParams.name) {
      queryParams = queryParams.set('name', <any>customerQueryParams.name);
    }
    if (customerQueryParams.username) {
      queryParams = queryParams.set('username', <any>customerQueryParams.username);
    }
    if (customerQueryParams.surname) {
      queryParams = queryParams.set('surname', <any>customerQueryParams.surname);
    }
    if (customerQueryParams.email) {
      queryParams = queryParams.set('email', <any>customerQueryParams.email);
    }
    if (customerQueryParams.status) {
      queryParams = queryParams.set('status', <any>customerQueryParams.status);
    }
    if (customerQueryParams.startDate) {
      queryParams = queryParams.set('startDate', <any>customerQueryParams.startDate);
    }
    if (customerQueryParams.endDate) {
      queryParams = queryParams.set('endDate', <any>customerQueryParams.endDate);
    }
    if (customerQueryParams.creationStartDate) {
      queryParams = queryParams.set('creationStartDate', <any>customerQueryParams.creationStartDate);
    }
    if (customerQueryParams.creationEndDate) {
      queryParams = queryParams.set('creationEndDate', <any>customerQueryParams.creationEndDate);
    }
    if (customerQueryParams.updateStartDate) {
      queryParams = queryParams.set('updateStartDate', <any>customerQueryParams.updateStartDate);
    }
    if (customerQueryParams.updateEndDate) {
      queryParams = queryParams.set('updateEndDate', <any>customerQueryParams.updateEndDate);
    }
    if (customerQueryParams.product) {
      queryParams = queryParams.set('product', <any>customerQueryParams.product);
    }
    if (customerQueryParams.sortParam && customerQueryParams.sortParam.length) {
      queryParams = queryParams.set('sortParam', <any>customerQueryParams.sortParam);
    }

    if (customerQueryParams.subscriberStatus !== undefined && customerQueryParams.subscriberStatus !== null) {
      queryParams = queryParams.set('subscriberStatus', <any>customerQueryParams.subscriberStatus);
    }

    if (customerQueryParams.type) {
      queryParams = queryParams.set('type', <any>customerQueryParams.type);
    }
    if (customerQueryParams.accountId) {
      queryParams = queryParams.set('accountId', <any>customerQueryParams.accountId);
    }
    if (customerQueryParams.serviceId) {
      queryParams = queryParams.set('serviceId', <any>customerQueryParams.serviceId);
    }
    if (customerQueryParams.boxDeviceId) {
      queryParams = queryParams.set('boxDeviceId', <any>customerQueryParams.boxDeviceId);
    }
    if (customerQueryParams.packageId) {
      queryParams = queryParams.set('packageId', <any>customerQueryParams.packageId);
    }
    if (customerQueryParams.profileUid) {
      queryParams = queryParams.set('profileUid', <any>customerQueryParams.profileUid);
    }
    if (customerQueryParams.demoCampaignId) {
      queryParams = queryParams.set('demoCampaignId', <any>customerQueryParams.demoCampaignId);
    }
    if (customerQueryParams.excludeDemo) {
      queryParams = queryParams.set('excludeDemo', <any>customerQueryParams.excludeDemo);
    }

    return this.http.get<any>(this.baseUrl + '/pageable', { params: queryParams, observe: 'response' });
  }

  getCrmReportRequest(page?: number, type?: string[]): Observable<PageOfReportRequest> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (page != undefined) {
      queryParams = queryParams.set('page', <any>page);
    }
    if (type) {
      queryParams = queryParams.set('type', <any>type);
      queryParams = queryParams.set('itemCount', <any>5);
    }

    return this.http.get<PageOfReportRequest>(this.reportUrl, { params: queryParams });
  }

  customerReport(customer?: CustomerQueryParams): Observable<string> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (customer.name) {
      queryParams = queryParams.set('name', <any>customer.name);
    }
    if (customer.surname) {
      queryParams = queryParams.set('surname', <any>customer.surname);
    }
    if (customer.email) {
      queryParams = queryParams.set('email', <any>customer.email);
    }
    return this.http.get<string>(this.baseUrl + '/report', { params: queryParams });
  }

  getPurchasedVodsByCustomer(customerID: number, type: string, startDate: number, endDate: number): Observable<ResponseObj> {
    let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (customerID !== undefined && customerID !== null) {
      queryParameters = queryParameters.set('customerId', <any>customerID);
    }
    if (type !== undefined && type !== null) {
      queryParameters = queryParameters.set('type', <any>type);
    }
    if (startDate !== undefined && startDate !== null) {
      queryParameters = queryParameters.set('startDate', <any>startDate);
    }
    if (endDate !== undefined && endDate !== null) {
      queryParameters = queryParameters.set('endDate', <any>endDate);
    }
    return this.http.get(this.purchaseVodsUrl, { params: queryParameters });
  }

  getPurchasedVodsCount(date?: number): Observable<ResponseObj> {
    let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (date !== undefined && date !== null) {
      queryParameters = queryParameters.set('date', <any>date);
    }
    return this.http.get(this.purchaseVodsUrl + '/count', { params: queryParameters });
  }

  getTotalPayment(customerID: number, type: string, startDate: number, endDate: number): Observable<ResponseObj> {
    let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (customerID !== undefined && customerID !== null) {
      queryParameters = queryParameters.set('customerId', <any>customerID);
    }
    if (type !== undefined && type !== null) {
      queryParameters = queryParameters.set('type', <any>type);
    }
    if (startDate !== undefined && startDate !== null) {
      queryParameters = queryParameters.set('startDate', <any>startDate);
    }
    if (endDate !== undefined && endDate !== null) {
      queryParameters = queryParameters.set('endDate', <any>endDate);
    }
    return this.http.get(this.purchaseVodsUrl + '/payment', { params: queryParameters });
  }

  addCustomer(credentials: any): Observable<any> {
    this.userExistsSubject.next(null);

    // New API format - direct CustomerCreateRequest
    const customerRequest = {
      username: credentials.username,
      email: credentials.email,
      password: credentials.password,
      name: credentials.name,
      surname: credentials.lastName,
      gsmno: credentials.gsmno,
      address: credentials.address,
      birthDate: credentials.birthDate,
      gender: credentials.gender,
      city: credentials.city,
      type: canonicalCustomerType(credentials.type),
      nPvrEnabled: credentials.npvrEnabled,
      accountId: credentials.accountId,
      serviceId: credentials.serviceId,
      customerGroupIds: credentials.customerGroups ? credentials.customerGroups.map((g: any) => g.id) : []
    };

    return this.http.post(this.baseUrl, customerRequest).pipe(
      tap((response: any) => {
        if (response && response.success) {
          this.userExistsSubject.next(false);
        } else {
          this.userExistsSubject.next(true);
        }
      }),
      catchError((error) => {
        this.userExistsSubject.next(true);
        return of(null);
      }),
    );
  }

  importAccountMigration(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(this.baseUrl + '/account-migration/import', formData);
  }

  getAccountMigrationImportJob(jobId: string): Observable<any> {
    return this.http.get(this.baseUrl + '/account-migration/import/' + jobId);
  }

  getCustomer(id): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/' + id);
  }

  editCustomer(inputCustomer): Observable<ResponseObj> {
    // Transform InputCustomer (with customerP wrapper) to CustomerUpdateRequest format
    const customer = inputCustomer.customerP || inputCustomer;

    const customerRequest = {
      id: customer.id,
      username: customer.username,
      email: customer.email,
      password: customer.password,
      name: customer.name,
      surname: customer.surname,
      gsmno: customer.gsmno,
      address: customer.address,
      birthDate: customer.birthDate,
      gender: customer.gender,
      city: customer.city,
      status: customer.status,
      type: canonicalCustomerType(customer.type),
      externalId: customer.externalId,
      nPvrEnabled: customer.npvrEnabled,
      npvrQuota: customer.npvrQuota,
      pin: customer.pin,
      languageCode: customer.languageCode,
      recordDurationQuota: customer.recordDurationQuota,
      recordStorageQuota: customer.recordStorageQuota,
      limitlessRecordDuration: customer.limitlessRecordDuration,
      limitlessRecordStorage: customer.limitlessRecordStorage,
      accountId: customer.accountId,
      serviceId: customer.serviceId,
      customerGroupIds: customer.customerGroups ? customer.customerGroups.map((g: any) => g.id) : []
    };

    return this.http.put(this.baseUrl, customerRequest);
  }

  filterSearchCustomer(name, email, surname, city, status?: string[]): Observable<ResponseObj> {
    let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (name !== undefined && name !== null && name != '') {
      queryParameters = queryParameters.set('name', <any>name);
    }
    if (email !== undefined && email !== null && email != '') {
      queryParameters = queryParameters.set('email', <any>email);
    }
    if (surname !== undefined && surname !== null && surname != '') {
      queryParameters = queryParameters.set('surname', <any>surname);
    }
    if (city !== undefined && city !== null && city != '') {
      queryParameters = queryParameters.set('city', <any>city);
    }
    if (status && status.length > 0) {
      queryParameters = queryParameters.set('status', <any>status);
    }
    return this.http.get(this.baseUrl + '/search', { params: queryParameters });
  }

  deleteCustomer(inputObj): Observable<ResponseObj> {
    return this.http.delete(this.baseUrl + '/' + inputObj.customerP.id);
  }

  listProducts(): Observable<ResponseObj> {
    return this.http.get(this.productUrl + '/search');
  }
  readonly subscriptionApiUrl = environment.mwCrmEndpoint + Service.SUBSCRIPTION_API;

  addSubsriptionToCustomer(inputObj): Observable<ResponseObj> {
    inputObj.username = this.currentUser.username;
    const headers = new HttpHeaders()
      .set('user-id', this.currentUser.id.toString())
      .set('username', this.currentUser.username);
    return this.http.post(this.subscriptionApiUrl + '/subscribe', inputObj, { headers });
  }

  customerCountByStatus(status): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/count/status/' + status);
  }

  customerCountGroupByStatus(excludeDemo = false): Observable<ResponseObj> {
    const params = new HttpParams().set('excludeDemo', excludeDemo.toString());
    return this.http.get(this.baseUrl + '/count/group-by-status', { params });
  }

  updateSubscriptionStatus(id: number, status: string): Observable<ResponseObj> {
    const headers = new HttpHeaders()
      .set('user-id', this.currentUser.id.toString())
      .set('username', this.currentUser.username);

    // Using SubscriptionRequest format expected by backend: { status: '...' }
    return this.http.put(this.subscriptionApiUrl + '/' + id + '/status', { status: status }, { headers });
  }

  unsubscribeSubscription(id: number): Observable<ResponseObj> {
    const headers = new HttpHeaders()
      .set('user-id', this.currentUser.id.toString())
      .set('username', this.currentUser.username);
    return this.http.put(this.subscriptionApiUrl + '/' + id + '/unsubscribe', {}, { headers });
  }

  getAllSubscriptions(): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.subscriberUrl + '/getall', { observe: 'response' });
  }

  getDubSubOptions(id): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/' + id + '/dub-sub-options');
  }

  deleteDubSub(id): Observable<ResponseObj> {
    return this.http.delete(this.baseUrl + '/dub-sub-options/' + id);
  }

  getBtvReminders(customerId): Observable<ResponseObj> {
    return this.http.get(this.reminderUrl + '?userId=' + customerId);
  }

  deleteReminder(id): Observable<ResponseObj> {
    return this.http.delete(this.reminderUrl + '/' + id);
  }

  deleteReminderBulk(): Observable<ResponseObj> {
    return this.http.delete(this.reminderUrl + '/bulk');
  }

  getSessionQuotas(type?: string): Observable<ResponseObj> {
    return this.http.get(this.sessionQuotaUrl + '?deviceType=' + type);
  }

  getSessionQuotasByCustomerId(customerId?: number, type: string = ''): Observable<ResponseObj> {
    return this.http.get(this.sessionQuotaUrl + '?userId=' + customerId + '&deviceType=' + type);
  }

  deleteSessionQuotas(id): Observable<ResponseObj> {
    return this.http.delete(this.sessionQuotaUrl + '/' + id);
  }

  createSessionQuota(userDeviceSessionQuota): Observable<ResponseObj> {
    return this.http.post(this.sessionQuotaUrl, userDeviceSessionQuota);
  }

  editSessionQuota(userDeviceSessionQuota): Observable<ResponseObj> {
    return this.http.put(this.sessionQuotaUrl, userDeviceSessionQuota);
  }

  getUserDevices(customerId, type: string = ''): Observable<ResponseObj> {
    return this.http.get(this.deviceUrl + '/' + customerId);
  }

  deleteUserDevices(id): Observable<ResponseObj> {
    return this.http.delete(this.deviceUrl + '/registered/' + id);
  }

  addBulkQuota(inputObj: any): Observable<ResponseObj> {
    return this.http.put(this.baseUrl + '/npvr-quota/bulk', inputObj);
  }

  resetPassword(customerID: number): Observable<ResponseObj> {
    return this.http.post(this.baseUrl + `/${customerID}/resetpassword`, null);
  }

  changePassword(customerId: number, newPassword: string): Observable<ResponseObj> {
    return this.http.post(this.baseUrl + '/change-password', {
      customerId: customerId,
      newPassword: newPassword
    });
  }

  resetPasswordChange(email: string): Observable<number> {
    const params = new HttpParams().set('email', email);

    return this.http.post<number>(
      this.baseUrl + '/forgot-password-new',
      null,
      { params }
    );
  }

  deletePin(customerID: number): Observable<ResponseObj> {
    return this.http.delete(this.baseUrl + `/${customerID}/pin`);
  }

  getLockedChannels(customerID: number, profileUid: string): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/locked-channels',
      { headers: new HttpHeaders({ 'X-Skip-Error-Toast': 'true' }) });
  }

  addLockedChannel(customerID: number, profileUid: string, payload: { channelId: number; channelName?: string; channelPoster?: string }): Observable<any> {
    return this.http.post<any>(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/locked-channels', payload);
  }

  removeLockedChannel(customerID: number, profileUid: string, lockedChannelId: number): Observable<any> {
    return this.http.delete<any>(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/locked-channels/' + lockedChannelId);
  }

  // Every channel with this profile's favorite / hidden / locked / restricted
  // flags resolved server-side, so the UI does not have to join the four
  // preference stores.
  getChannelPreferences(customerID: number, profileUid: string): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/channel-preferences');
  }

  getHiddenChannels(customerID: number, profileUid: string): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/hidden-channels');
  }

  addHiddenChannels(customerID: number, profileUid: string, channelUIds: string[]): Observable<any> {
    return this.http.post<any>(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/hidden-channels',
      { channelUIds: channelUIds });
  }

  removeHiddenChannels(customerID: number, profileUid: string, channelUIds: string[]): Observable<any> {
    return this.http.request<any>('delete', this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/hidden-channels',
      { body: { channelUIds: channelUIds } });
  }

  getBannedChannels(customerID: number, profileUid: string): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/banned-channels');
  }

  addBannedChannels(customerID: number, profileUid: string, channelUIds: string[]): Observable<any> {
    return this.http.post<any>(this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/banned-channels',
      { channelUIds: channelUIds });
  }

  removeBannedChannels(customerID: number, profileUid: string, channelUIds: string[]): Observable<any> {
    return this.http.request<any>('delete', this.baseUrl + '/' + customerID + '/profiles/' + profileUid + '/banned-channels',
      { body: { channelUIds: channelUIds } });
  }

  getCustomerRecords(customerID: number): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/' + customerID + '/npvr');
  }

  deleteCustomerRecord(customerID: number, recordUid: string): Observable<any> {
    return this.http.delete(this.baseUrl + '/' + customerID + '/npvr/' + recordUid);
  }

  getDailyCustomerSubChanges(): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/subscription-count');
  }

  getSubCountbyMainPackage(): Observable<ResponseObj> {
    return this.http.get(this.baseUrl + '/subscriber-count');
  }

  createCampaign(campaign: Campaign): Observable<Campaign> {
    return this.http.post<Campaign>(this.campaignUrl, campaign);
  }
  createPromotions(count: number, prefix: string, campaignId: number): Observable<void> {
    const params = new HttpParams().set('count', count.toString()).set('prefix', prefix).set('campaignId', campaignId.toString());

    return this.http.post<void>(this.campaignUrl + '/promotions', {}, { params });
  }

  editCampaign(campaign: Campaign): Observable<Campaign> {
    return this.http.put<Campaign>(this.campaignUrl + '/', campaign);
  }

  deleteCampaign(id: number): Observable<ResponseObj> {
    return this.http.delete(this.campaignUrl + `/${id}`);
  }

  getCampaign(id: number): Observable<Campaign> {

    return this.http.get<Campaign>(this.campaignUrl + `/${id}`);
  }

  getAllCampaigns(queryParams?: QueryParams): Observable<PageOfCampaigns> {
    let queryParam = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    queryParams.page = queryParams.page - 1;
    if (queryParams.page > 0) {
      queryParam = queryParam.set('page', queryParams.page.toString());
    }
    if (queryParams.itemCount) {
      queryParam = queryParam.set('size', queryParams.itemCount.toString());
    }
    if (queryParams.name) {
      queryParam = queryParam.set('name', queryParams.name.toString());
    }
    if (queryParams.sortParam && queryParams.sortParam.length > 0) {
      queryParam = queryParam.set('sortParam', queryParams.sortParam.join(','));
    }
    if (queryParams.isDeleted !== undefined) {
      queryParam = queryParam.set('isDeleted', queryParams.isDeleted.toString());
    }

    if (queryParams.endDate) {
      queryParam = queryParam.set('endDate', queryParams.endDate.toString());
    }
    if (queryParams.startDate) {
      queryParam = queryParam.set('startDate', queryParams.startDate.toString());
    }

    return this.http.get<PageOfCampaigns>(this.campaignUrl, { params: queryParam });
  }
  getPromotions(queryParams?: QueryParams): Observable<PageOfPromotions> {
    let queryParam = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    queryParams.page = queryParams.page - 1;
    if (queryParams.page > 0) {
      queryParam = queryParam.set('page', queryParams.page.toString());
    }
    if (queryParams.itemCount) {
      queryParam = queryParam.set('size', queryParams.itemCount.toString());
    }
    if (queryParams.promotionStatus) {
      queryParam = queryParam.set('status', queryParams.promotionStatus.toString());
    }
    if (queryParams.campaignId) {
      queryParam = queryParam.set('campaignId', queryParams.campaignId.toString());
    }
    if (queryParams.sortParam && queryParams.sortParam.length > 0) {
      queryParam = queryParam.set('sortParam', queryParams.sortParam.join(','));
    }
    return this.http.get<PageOfPromotions>(this.campaignUrl + '/promotions', { params: queryParam });
  }

  changeStatus(customerId: string, status: string): Observable<any> {
    return this.http.put(
      this.baseUrl + '/' + customerId + '/status?status=' + status,
      {}
    );
  }

  getUserActivities(): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.baseUrl + "/user-activity-count", { observe: 'response' });
  }

  getOnlineUserActivity(): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.baseUrl + "/online-user-activity-count", { observe: 'response' });
  }

  getOnlineDeviceActivity(): Observable<HttpResponse<any>> {
    return this.http.get<any>(this.baseUrl + "/online-device-activity-count", { observe: 'response' });
  }

  // Profile Permissions Management
  getCustomerProfiles(customerId: number): Observable<any> {
    return this.http.get(this.baseUrl + '/' + customerId + '/profiles');
  }

  updateLoginPreferences(customerId: number, preferences: any): Observable<any> {
    return this.http.put(this.baseUrl + '/' + customerId + '/login-preferences', preferences);
  }

  updateProfilePermissions(customerId: number, profileUid: string, permissions: any): Observable<any> {
    return this.http.put(this.baseUrl + '/' + customerId + '/profiles/' + profileUid + '/permissions', permissions);
  }

  // KODSIS Integration Methods
  searchByKodsisParams(accountId?: number, serviceId?: number, boxDeviceId?: string, status?: string[], packageId?: number, subscriberStatus?: boolean): Observable<any> {
    let queryParams = new HttpParams();

    if (accountId) {
      queryParams = queryParams.set('accountId', accountId.toString());
    }
    if (serviceId) {
      queryParams = queryParams.set('serviceId', serviceId.toString());
    }
    if (boxDeviceId) {
      queryParams = queryParams.set('boxDeviceId', boxDeviceId);
    }
    if (status && status.length > 0) {
      queryParams = queryParams.set('status', <any>status);
    }
    if (packageId) {
      queryParams = queryParams.set('packageId', packageId.toString());
    }
    if (subscriberStatus !== undefined && subscriberStatus !== null) {
      queryParams = queryParams.set('subscriberStatus', subscriberStatus.toString());
    }

    return this.http.get(this.baseUrl + '/kodsis', { params: queryParams });
  }

  suspendCustomer(customerId: number): Observable<ResponseObj> {
    return this.http.post(this.baseUrl + '/' + customerId + '/suspend', null);
  }

  unsuspendCustomer(customerId: number): Observable<ResponseObj> {
    return this.http.post(this.baseUrl + '/' + customerId + '/unsuspend', null);
  }

  resetPasswordKodsis(customerId: number): Observable<ResponseObj> {
    return this.http.post(this.baseUrl + '/' + customerId + '/reset-password', null);
  }

  getFavorites(customerId: number): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + '/' + customerId + '/favorites');
  }

  getFavoritesByProfile(customerId: number, profileUid: string): Observable<any[]> {
    const params = new HttpParams().set('profileUid', profileUid);
    return this.http.get<any[]>(this.baseUrl + '/' + customerId + '/favorites/by-profile', { params });
  }

  deleteFavorite(customerId: number, favoriteId: string): Observable<any> {
    return this.http.delete(this.baseUrl + '/' + customerId + '/favorites/' + favoriteId);
  }

  updateFavoritesOrder(customerId: number, items: { id: string; displayOrder: number }[]): Observable<any> {
    return this.http.put(this.baseUrl + '/' + customerId + '/favorites/order', { items });
  }

  getAvailableChannels(customerId: number, profileUid: string, search?: string, limit?: number): Observable<any[]> {
    let params = new HttpParams().set('profileUid', profileUid);
    if (search) {
      params = params.set('search', search);
    }
    if (limit != null) {
      params = params.set('limit', limit.toString());
    }
    return this.http.get<any[]>(this.baseUrl + '/' + customerId + '/favorites/available-channels', { params });
  }

  getAvailableVods(customerId: number, profileUid: string, search?: string, limit?: number): Observable<any[]> {
    let params = new HttpParams().set('profileUid', profileUid);
    if (search) {
      params = params.set('search', search);
    }
    if (limit != null) {
      params = params.set('limit', limit.toString());
    }
    return this.http.get<any[]>(this.baseUrl + '/' + customerId + '/favorites/available-vods', { params });
  }

  addFavorite(customerId: number, payload: { profileUid: string; type: string; referenceId: string }): Observable<any> {
    return this.http.post(this.baseUrl + '/' + customerId + '/favorites', payload);
  }

  getPlaylists(customerId: number): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + '/' + customerId + '/playlists');
  }

  deletePlaylistItem(customerId: number, itemId: string): Observable<any> {
    return this.http.delete(this.baseUrl + '/' + customerId + '/playlists/items/' + itemId);
  }


  getReactions(customerId: number): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + '/' + customerId + '/reactions');
  }

  getReminders(customerId: number): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + '/' + customerId + '/reminders');
  }

  // Box Device ID Management
  checkBoxDeviceId(customerId: number, boxDeviceId: string): Observable<any> {
    let params = new HttpParams().set('boxDeviceId', boxDeviceId);
    return this.http.get(this.baseUrl + '/' + customerId + '/box-device-id/check', { params });
  }

  updateBoxDeviceId(customerId: number, boxDeviceId: string, force: boolean = false): Observable<any> {
    let params = new HttpParams()
      .set('boxDeviceId', boxDeviceId)
      .set('force', force.toString());
    return this.http.put(this.baseUrl + '/' + customerId + '/box-device-id', {}, { params });
  }

  getCustomerNotes(customerId: number, page?: number, itemCount?: number): Observable<any> {
    let queryParams: HttpParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (page) {
      queryParams = queryParams.set('page', <any>page);
    }
    if (itemCount) {
      queryParams = queryParams.set('itemCount', <any>itemCount);
    }

    return this.http.get<any>(this.baseUrl + `/${customerId}/notes`, { params: queryParams });
  }

  addCustomerNote(noteObj: any, id: number): Observable<any> {
    return this.http.post<any>(this.baseUrl + `/${id}/notes`, noteObj);
  }

  deleteCustomerNote(id: number): Observable<any> {
    return this.http.delete<any>(this.baseUrl + `/${id}/notes`);
  }

  editCustomerNote(noteObj: any): Observable<any> {
    return this.http.put<any>(this.baseUrl + `/notes`, noteObj);
  }

  // NPVR Channel Permissions
  getNpvrPermissions(customerId: number): Observable<any> {
    return this.http.get<any>(this.baseUrl + '/' + customerId + '/npvr-permissions');
  }

  addNpvrBlockedChannel(customerId: number, payload: { channelId: number; channelName: string; channelPosterUrl?: string }): Observable<any> {
    return this.http.post<any>(this.baseUrl + '/' + customerId + '/npvr-permissions', payload);
  }

  removeNpvrBlockedChannel(customerId: number, permissionId: number): Observable<any> {
    return this.http.delete<any>(this.baseUrl + '/' + customerId + '/npvr-permissions/' + permissionId);
  }

}
